import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  ITextInputComponent, 
  INumberInputComponent, 
  IEmailInputComponent, 
  TInputComponentType,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';
import { ThresholdAlert } from '../../ThresholdAlert';

interface DfFormInputProps {
  id: string;
  properties: ITextInputComponent | INumberInputComponent | IEmailInputComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  inputType?: TInputComponentType;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  formTemplateId?: string;
  onThresholdIssueRaised?: (conditionId: string) => void;
  raisedThresholdIssues?: Set<string>; // Track which threshold issues have been raised
}

export const DfFormInput: React.FC<DfFormInputProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  inputType = 'text',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'test',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false,
  formTemplateId,
  onThresholdIssueRaised,
  raisedThresholdIssues = new Set()
}) => {
  // Ensure formValue is always a string to prevent [object Object] errors
  const getStringValue = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'number') return String(val);
    if (typeof val === 'boolean') return String(val);
    // If it's an object, try to extract a meaningful value or return empty string
    if (typeof val === 'object') {
      if (Array.isArray(val)) return '';
      // Try common object properties
      if ('value' in val) return getStringValue(val.value);
      if ('label' in val) return getStringValue(val.label);
      return '';
    }
    return String(val || '');
  };

  // CRITICAL: Use a ref to track the component's unique ID to prevent value sharing
  const componentIdRef = useRef<string>(id);
  
  // Update ref if ID changes (shouldn't happen, but safety check)
  useEffect(() => {
    if (id !== componentIdRef.current) {
      console.warn(`[DfFormInput] Component ID changed from ${componentIdRef.current} to ${id}`);
      componentIdRef.current = id;
    }
  }, [id]);
  
  const initialValue = getStringValue(formValue || properties?.basic?.defaultValue || '');
  const [value, setValue] = useState<string>(initialValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());
  const [previousValue, setPreviousValue] = useState<string>(initialValue);

  // Determine input type based on component name
  const getInputType = (): TInputComponentType => {
    if (inputType) return inputType;
    
    const name = properties?.name;
    if (name === 'text-input') return 'text';
    if (name === 'number-input') return 'number';
    if (name === 'email-input') return 'email';
    
    return 'text';
  };

  // Build validation rules
  const buildValidationRules = () => {
    const rules: any = {};
    
    if (properties?.validation?.required) {
      rules.required = true;
    }
    
    if (getInputType() === 'email') {
      rules.email = true;
    }
    
    if (getInputType() === 'text') {
      const textValidation = properties.validation as ITextInputComponent['validation'];
      if (textValidation?.minLength) rules.minLength = textValidation.minLength;
      if (textValidation?.maxLength) rules.maxLength = textValidation.maxLength;
    }
    
    if (getInputType() === 'number') {
      const numberValidation = properties.validation as INumberInputComponent['validation'];
      if (numberValidation?.min) rules.min = numberValidation.min;
      if (numberValidation?.max) rules.max = numberValidation.max;
      if (numberValidation?.lowerLimit !== undefined) rules.lowerLimit = numberValidation.lowerLimit;
      if (numberValidation?.upperLimit !== undefined) rules.upperLimit = numberValidation.upperLimit;
    }
    
    return rules;
  };

  // Validate field
  const validateField = useCallback((val: string): { isValid: boolean; errors: Record<string, any> } => {
    const rules = buildValidationRules();
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (rules.required && (!val || val.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    // Email validation
    if (rules.email && val && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)) {
      errors.email = true;
      isValid = false;
    }

    // Length validations
    if (rules.minLength && val && val.length < rules.minLength) {
      errors.minLength = true;
      isValid = false;
    }

    if (rules.maxLength && val && val.length > rules.maxLength) {
      errors.maxLength = true;
      isValid = false;
    }

    // Number validations
    if (getInputType() === 'number' && val) {
      const numValue = parseFloat(val);
      if (isNaN(numValue)) {
        errors.pattern = true;
        isValid = false;
      } else {
        if (rules.min !== undefined && numValue < rules.min) {
          errors.min = true;
          isValid = false;
        }
        if (rules.max !== undefined && numValue > rules.max) {
          errors.max = true;
          isValid = false;
        }
        if (rules.lowerLimit !== undefined && numValue < rules.lowerLimit) {
          errors.lowerLimit = true;
          isValid = false;
        }
        if (rules.upperLimit !== undefined && numValue > rules.upperLimit) {
          errors.upperLimit = true;
          isValid = false;
        }
      }
    }

    return { isValid, errors };
  }, [properties, getInputType]);

  // Handle value changes
  // CRITICAL: Use componentIdRef to ensure we're using the correct component ID
  const handleValueChange = useCallback((newValue: string) => {
    setValue(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      // CRITICAL: Use componentIdRef.current to ensure we're using the correct ID
      // This prevents ID collisions if the id prop somehow changes
      const currentId = componentIdRef.current;
      const change: IFormControlChange = {
        id: currentId, // Use the ref to ensure correct ID
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      
      // Debug: Log value changes to identify issues
      if (currentId !== id) {
        console.error(`[DfFormInput] ID mismatch in handleValueChange! ref: ${currentId}, prop: ${id}`);
      }
      
      onValueChange(change);
    }
  }, [id, onValueChange, validateField, componentIdRef]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    handleValueChange(newValue);
  };

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    
    setIsFocused(false);
    setIsTouched(true);
    
    // Always trigger validation on blur in test mode for any field with validation rules
    if (mode === 'test' && onValueChange) {
      const validation = validateField(value);
      const change: IFormControlChange = {
        id,
        value: value,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      
      onValueChange(change);
    }
    
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Reset touched state and value when switching modes
  useEffect(() => {
    if (mode === 'edit') {
      setIsTouched(false);
      // Reset value to default value when switching to edit mode
      const defaultValue = properties?.basic?.defaultValue || '';
      setValue(defaultValue);
    } else if (mode === 'test') {
      setIsTouched(false);
      // Reset value to empty when switching to test mode for fresh start
      setValue('');
    }
  }, [mode, properties?.basic?.defaultValue]);

  // Update value when formValue prop changes (but don't override user input)
  // CRITICAL: Only update if formValue is actually for THIS component's ID
  // Use componentIdRef to ensure we're checking against the correct component ID
  useEffect(() => {
    const currentComponentId = componentIdRef.current;
    const newValue = getStringValue(formValue || properties?.basic?.defaultValue || '');
    
    // CRITICAL: Only update if this formValue is actually meant for this component
    // Check if formValue matches what should be stored for this component's ID
    // This prevents cross-component value syncing when IDs are duplicated
    
    // Only update if the new value is different and we're not currently focused
    // Also, don't override with empty value if user has typed something
    // CRITICAL FIX: Don't reset user input when formValue becomes empty
    if (newValue !== value && !isFocused && !(newValue === '' && String(value || '').trim() !== '')) {
      // Additional check: Only update if formValue is not undefined/null (meaning it's actually set for this component)
      // If formValue is undefined, it means this component hasn't been initialized yet, so don't override local state
      if (formValue !== undefined && formValue !== null) {
        // Double-check: Only update if the ID matches (safety check against ID collisions)
        if (currentComponentId === id) {
          setValue(newValue);
        } else {
          console.error(`[DfFormInput] ID mismatch! componentIdRef: ${currentComponentId}, prop id: ${id}`);
        }
      }
    } else if (newValue === '' && String(value || '').trim() !== '' && !isFocused) {
      // Don't clear user input
    }
  }, [formValue, value, isFocused, properties?.basic?.defaultValue, id]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Clear value when switching away from test mode
  useEffect(() => {
    if (mode !== 'test') {
      setValue('');
    }
  }, [mode]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get input classes
  const getInputClasses = (): string => {
    const base = 'form-control';
    
    // Only show validation errors in test mode
    if (mode === 'test') {
      const validation = validateField(value);
      const hasLocalError = !validation.isValid && isTouched;
      const hasExternalError = validationErrors[id] && (isTouched || formSubmitted);
      const error = (hasLocalError || hasExternalError) ? 'is-invalid' : '';
      return `${base} ${error} ${className}`.trim();
    }
    
    return `${base} ${className}`.trim();
  };

  // Evaluate threshold conditions
  const evaluateThresholdCondition = useCallback((condition: any, currentVal: string | number): boolean => {
    if (!currentVal || currentVal === '') return false;
    
    const numValue = typeof currentVal === 'string' ? parseFloat(currentVal) : currentVal;
    const thresholdValue = typeof condition.value === 'string' ? parseFloat(condition.value) : condition.value;
    
    if (isNaN(numValue) || isNaN(thresholdValue)) return false;

    switch (condition.operator) {
      case 'greaterThan':
        return numValue > thresholdValue;
      case 'lessThan':
        return numValue < thresholdValue;
      case 'greaterThanOrEqual':
        return numValue >= thresholdValue;
      case 'lessThanOrEqual':
        return numValue <= thresholdValue;
      case 'equals':
        return numValue === thresholdValue;
      case 'notEquals':
        return numValue !== thresholdValue;
      default:
        return false;
    }
  }, []);

  // Get condition conditions and evaluate them
  const thresholdConditions = useMemo(() => {
    return (properties as any)?.condition?.conditions || [];
  }, [(properties as any)?.condition]);

  // Clear dismissed alerts when value changes
  useEffect(() => {
    if (value !== previousValue) {
      setDismissedAlerts(new Set());
      setPreviousValue(value);
    }
  }, [value, previousValue]);

  const activeThresholdCondition = useMemo(() => {
    if (mode !== 'test' || !value || value === '') {
      return null;
    }
    
    // Check which conditions are currently met
    const metConditions = thresholdConditions.filter((condition: any) => 
      evaluateThresholdCondition(condition, value)
    );
    
    // If no conditions are met, return null
    if (metConditions.length === 0) {
      return null;
    }
    
    // Find the first condition that is met and not dismissed
    // Note: We still return the condition even if an issue is raised, so we can show the indicator
    for (const condition of metConditions) {
      if (!dismissedAlerts.has(condition.id)) {
        return condition;
      }
    }
    
    // If all met conditions are dismissed, return null (alert won't show)
    return null;
  }, [value, thresholdConditions, dismissedAlerts, mode, evaluateThresholdCondition, raisedThresholdIssues]);


  const handleDismissAlert = useCallback((conditionId: string) => {
    setDismissedAlerts(prev => new Set(prev).add(conditionId));
  }, []);

  const currentInputType = getInputType();
  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <div className={`form-group ${getLabelAlignmentClass()}`}>
      {!hideLabel && properties.basic.label && (
        <label htmlFor={id} className="form-label">
          {properties.basic.label}
          {properties.validation.required && (
            <span className="required-indicator">*</span>
          )}
        </label>
      )}

      <div className="input-wrapper">
        <input
          type={currentInputType}
          id={id}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={properties.basic.placeholder}
          disabled={shouldDisable}
          readOnly={readonly || properties?.validation?.readonly}
          className={getInputClasses()}
        />

        <DfFormErrorMsg
          validationErrors={validationErrors}
          fieldId={id}
          touchedFields={touchedFields}
          formSubmitted={formSubmitted}
          properties={properties}
          localValidation={validateField(value)}
          isTouched={isTouched}
          mode={mode}
        />

        {/* Threshold Alert - Show when condition is met */}
        {activeThresholdCondition && mode === 'test' && (() => {
          const issueRaised = raisedThresholdIssues?.has(activeThresholdCondition.id) || false;
          return (
            <ThresholdAlert
              key={`${activeThresholdCondition.id}-${issueRaised}`}
              component={properties}
              condition={activeThresholdCondition}
              currentValue={value}
              thresholdValue={activeThresholdCondition.value}
              formTemplateId={formTemplateId}
              onDismiss={() => handleDismissAlert(activeThresholdCondition.id)}
              onIssueRaised={onThresholdIssueRaised}
              isIssueRaised={issueRaised}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default DfFormInput;
